import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { RootState } from 'store';

import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Drawer,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledDatePicker from 'components/LabelledDatePicker';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import { FormSection } from 'pages/ProjectManagement/styles';
import { useAddJobTaskMutation, useUpdateJobTaskByIdMutation } from 'services/busmanApi';
import { TaskRequest } from 'types/JobTypes';
import { DESCRIPTION_LIMIT, JOB_PRIORITY, TASK_STATUS } from 'utils/constants';
import { AddEditTaskSchema, TaskFromValues } from 'utils/schema';

interface TaskDrawerProps {
  initialValues: TaskRequest | null;
  open: boolean;
  isEditing?: boolean;
  onClose: () => void;
  setSnackBarMessage: (message: MessageType) => void;
}

const defaultTaskValues = {
  name: '',
  job_id: '',
  due_date: null,
  description: '',
  actual_hours: 0,
  is_active: false,
  assigned_to_id: '',
  estimated_hours: 0,
  percent_complete: '0',
  removed_in_scope: null,
  introduced_in_scope_id: '',
  priority: JOB_PRIORITY.LOW,
  status: TASK_STATUS.PENDING.value,
};

const TaskDrawer: React.FC<TaskDrawerProps> = ({ initialValues, open, onClose, isEditing, setSnackBarMessage }) => {
  const theme = useTheme();
  const [addJobTask] = useAddJobTaskMutation();
  const [updateJobTask] = useUpdateJobTaskByIdMutation();
  const { assigneeList, projectJobs, projectScopeList, selectedProjectScope } = useSelector(
    (state: RootState) => state.projectFilterReducer,
  );
  const { modalProps } = useSelector((state: RootState) => state.projectModalReducer);

  const jobId = modalProps?.data.job_id;
  const scopeId = modalProps?.data.introduced_in_scope_id;
  const scopeStatus = projectScopeList.find((project) => project.id === selectedProjectScope)?.status;

  const { handleSubmit, setValue, watch, control } = useForm({
    defaultValues: {
      ...defaultTaskValues,
      job_id: jobId,
      introduced_in_scope_id: scopeId,
      ...initialValues,
    },
    resolver: zodResolver(AddEditTaskSchema),
  });

  const onSubmit = async (data: TaskFromValues) => {
    const payload = {
      ...data,
      due_date: data.due_date ? dayjs(data.due_date).format('YYYY-MM-DD') : null,
    };

    if (isEditing) {
      const response = await updateJobTask({ taskId: data.id, updateTaskPayload: payload });
      if ('error' in response) {
        setSnackBarMessage({ type: 'error', msg: 'Error updating task' });
        return;
      }
      setSnackBarMessage({ type: 'success', msg: 'Task updated successfully.' });
    } else {
      const response = await addJobTask(payload);

      if ('error' in response) {
        setSnackBarMessage({ type: 'error', msg: 'Error adding task' });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Task added successfully.' });
    }

    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Task' : 'New Task'}</Typography>
            <Box onClick={onClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection>
            <Stack direction="row" gap={2}>
              <LabelledTextField dataTestId="task-input-name" name="name" label="TASK NAME" control={control} />
            </Stack>

            {jobId === null && scopeId === null && (
              <Stack direction="row">
                <Controller
                  name="job_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Stack direction="column" gap={2} width="100%">
                        <Typography variant="body2">JOB</Typography>
                        <Autocomplete
                          size="small"
                          options={projectJobs || []}
                          value={projectJobs?.find((item) => item.job_id === field.value) || null}
                          getOptionLabel={(option) => option?.job_name || ''}
                          isOptionEqualToValue={(option, value) => option.job_id === value?.job_id}
                          onChange={(event, value) => {
                            field.onChange(value?.job_id || '');
                            setValue('job_id', value?.job_id || '');
                            setValue('introduced_in_scope_id', value?.introduced_in_scope_id || '');
                          }}
                          ListboxProps={{
                            style: {
                              fontSize: '12px',
                            },
                          }}
                          sx={{ width: '100%' }}
                          data-testid="client-select-option"
                          popupIcon={<ExpandMoreIcon fontSize="small" />}
                          renderOption={(props, option) => {
                            // In recent MUI versions, key must be passed explicitly (not spread)
                            // eslint-disable-next-line react/prop-types
                            const { key, ...optionProps } = props;
                            return (
                              <Tooltip key={key} title={option?.job_description} placement="top">
                                <li {...optionProps}>{option?.job_name}</li>
                              </Tooltip>
                            );
                          }}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </Stack>
                    );
                  }}
                />
              </Stack>
            )}

            <Stack direction="row" gap={2}>
              <Stack direction="column" gap={2} width="100%">
                <Typography variant="body2">STATUS</Typography>
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Stack direction="column" gap={2} width="100%">
                        <Select
                          size="small"
                          value={field.value ?? ''}
                          data-testid="task-select-status"
                          sx={{ width: '100%' }}
                          onChange={(e) => setValue('status', e.target.value, { shouldValidate: true })}>
                          {Object.entries(TASK_STATUS)
                            .filter(([, item]) => scopeStatus !== 'DRAFT' || item.value === TASK_STATUS.PENDING.value)
                            .map(([key, item]) => (
                              <MenuItem key={key} value={item.value}>
                                {item.name}
                              </MenuItem>
                            ))}
                        </Select>
                        {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                      </Stack>
                    );
                  }}
                />
              </Stack>
            </Stack>

            <Stack direction="row" gap={2}>
              <LabelledTextField
                dataTestId="task-input-hours"
                name="actual_hours"
                label="ACTUAL HOURS"
                control={control}
              />
              <LabelledTextField
                dataTestId="task-input-est-hours"
                name="estimated_hours"
                label="EST. HOURS"
                control={control}
              />
            </Stack>

            <Box sx={{ my: 1 }}>
              <LabelledDatePicker dataTestId="task-due-date" name="due_date" label="DUE DATE" control={control} />
            </Box>

            <Box sx={{ my: 1 }}>
              <Stack direction="column" gap={2} width="100%">
                <Typography variant="body2">TASK PRIORITY</Typography>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Stack direction="column" gap={2} width="100%">
                        <Select
                          size="small"
                          value={field.value ?? ''}
                          data-testid="job-select-priority"
                          onChange={field.onChange}>
                          {Object.entries(JOB_PRIORITY).map(([key, value]) => (
                            <MenuItem key={key} value={value}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                      </Stack>
                    );
                  }}
                />
              </Stack>
            </Box>

            <Box sx={{ my: 1 }}>
              <Stack direction="column" gap={2} width="100%">
                <Typography variant="body2">ASSIGNED TO</Typography>
                <Controller
                  name="assigned_to_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Stack direction="column" gap={2} width="100%">
                        <Autocomplete
                          size="small"
                          options={assigneeList || []}
                          value={assigneeList?.find((user) => user.user_id === field.value) || null}
                          getOptionLabel={(option) => option.full_name || ''}
                          isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
                          onChange={(event, value) => {
                            field.onChange(value?.user_id || '');
                            setValue('assigned_to_id', value?.user_id || '');
                          }}
                          ListboxProps={{
                            style: {
                              fontSize: '12px',
                            },
                          }}
                          sx={{ width: '100%' }}
                          data-testid="client-select-option"
                          popupIcon={<ExpandMoreIcon fontSize="small" />}
                          renderInput={(params) => <TextField {...params} required error={!!error} />}
                        />
                      </Stack>
                    );
                  }}
                />
              </Stack>
            </Box>

            <Stack direction="row" gap={2}>
              <LabelledTextField name="percent_complete" label="% COMPLETE" control={control} />
            </Stack>

            <Stack direction="row" gap={2}>
              <LabelledTextField
                data-testid="job-input-description"
                name="description"
                label="DESCRIPTION"
                control={control}
                multiline
                placeholder="Text"
                rules={{ maxLength: DESCRIPTION_LIMIT }}
              />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2 }}>
              <Typography variant="caption" sx={{ color: theme.palette.busmanColors.busmanRed }}>
                {`${(watch('description') || '').length}/${DESCRIPTION_LIMIT}`}
              </Typography>
            </Box>

            <CustomButton
              text={`${isEditing ? 'Update' : 'Add'}`}
              variant="contained"
              width="medium"
              testid={'add-project-modal-btn'}
              onClick={handleSubmit(onSubmit)}
            />
          </FormSection>
        </Box>
      </Drawer>
    </>
  );
};

export default TaskDrawer;
